//@ts-nocheck
// 3-BOX — Full 4-screen emotional reading flow
// Tone: calm, gentle, respectful, low-intensity
// Screen 1: Box 1 — Inner Calm Type
// Screen 2: Box 2 — Emotional Rhythm / DOB
// Screen 3: Box 3 — Moment State
// Screen 4: Combined Result (Emotional Foundation + Rhythm + State + Summary)
// Language: the user's preferred language (Indonesian, else English)
import React, { useState } from "react";
import { useLaneCopy } from "../../utils/laneCopy";
import { useIndonesiaV2Language } from "./indonesiaV2/indonesiaV2Language";

// ─── Types ────────────────────────────────────────────────────────────────────
type Screen = "box1" | "box2" | "box3";
type InnerCalmType = "soft_calm" | "balanced_gentle" | "deep_quiet";
type MomentState =
  | "calm_moment"
  | "heavy_moment"
  | "need_space"
  | "quiet_focus";

export interface Indonesia3BoxData {
  inner_calm_type: InnerCalmType;
  dob: string;
  moment_state: MomentState;
}

// ─── Design tokens ───────────────────────────────────────────────────────────
const C = {
  bg: "#0f0a14", // warm off-white
  card: "#1a131f",
  border: "rgba(255,255,255,0.1)",
  beige: "#221a29",
  sand: "#2a2133",
  textDark: "#f7f3fa",
  textMuted: "#c8bfd1",
  accent: "#a78bff", // warm brown — calm, not purple-aggressive
  accentSoft: "rgba(167,139,255,0.14)",
  accentBorder: "rgba(167,139,255,0.4)",
  white: "#0f0a14",
};

// ─── Language Content ─────────────────────────────────────────────────────────
const CONTENT = {
  id: {
    back: "‹ Kembali",
    box1: {
      icon: "🌿",
      title: "Dasar Ketenangan Anda",
      subtitle: "Pilih yang paling mendekati cara Anda merasakan diri Anda.",
      options: [
        {
          key: "soft_calm" as InnerCalmType,
          label: "Tenang Lembut",
          desc: "Anda cenderung tenang dan tidak terburu-buru.",
        },
        {
          key: "balanced_gentle" as InnerCalmType,
          label: "Seimbang & Lembut",
          desc: "Anda lembut, stabil, dan mudah menyesuaikan diri.",
        },
        {
          key: "deep_quiet" as InnerCalmType,
          label: "Diam yang Dalam",
          desc: "Anda memproses banyak hal secara diam-diam dan mendalam.",
        },
      ],
      nextBtn: "Lanjut",
    },
    box2: {
      icon: "📅",
      title: "Tanggal Lahir Anda",
      subtitle: "Isi dengan tenang, tidak perlu terburu-buru.",
      microcopy: "Cukup isi sesuai yang Anda ingat.",
      dobPlaceholder: "TGL / BLN / THN",
      nextBtn: "Lihat Hasil",
    },
    box3: {
      icon: "🌊",
      title: "Keadaan Anda Saat Ini",
      subtitle: "Pilih yang paling mendekati perasaan Anda sekarang.",
      options: [
        {
          key: "calm_moment" as MomentState,
          label: "Momen Tenang",
          desc: "Anda merasa cukup tenang dan terkendali.",
        },
        {
          key: "heavy_moment" as MomentState,
          label: "Momen Berat",
          desc: "Ada sedikit beban yang Anda rasakan.",
        },
        {
          key: "need_space" as MomentState,
          label: "Butuh Ruang",
          desc: "Anda butuh ruang sejenak untuk menenangkan diri.",
        },
        {
          key: "quiet_focus" as MomentState,
          label: "Fokus Tenang",
          desc: "Anda sedang fokus dengan cara yang lembut dan diam.",
        },
      ],
      nextBtn: "Lanjut",
    },
    loading: "Sedang membaca ritme emosi Anda...",
  },
  en: {
    back: "‹ Back",
    box1: {
      icon: "🌿",
      title: "Your Inner Calm Foundation",
      subtitle: "Choose what best describes how you experience yourself.",
      options: [
        {
          key: "soft_calm" as InnerCalmType,
          label: "Soft Calm",
          desc: "You tend to be calm and unhurried.",
        },
        {
          key: "balanced_gentle" as InnerCalmType,
          label: "Balanced Gentle",
          desc: "You are gentle, stable, and adaptable.",
        },
        {
          key: "deep_quiet" as InnerCalmType,
          label: "Deep Quiet",
          desc: "You process many things silently and deeply.",
        },
      ],
      nextBtn: "Continue",
    },
    box2: {
      icon: "📅",
      title: "Your Date of Birth",
      subtitle: "Take your time, no need to rush.",
      microcopy: "Just fill in what you remember.",
      dobPlaceholder: "DD / MM / YYYY",
      nextBtn: "View Result",
    },
    box3: {
      icon: "🌊",
      title: "Your Current State",
      subtitle: "Choose what best describes how you feel right now.",
      options: [
        {
          key: "calm_moment" as MomentState,
          label: "Calm Moment",
          desc: "You feel fairly calm and in control.",
        },
        {
          key: "heavy_moment" as MomentState,
          label: "Heavy Moment",
          desc: "You're carrying a bit of weight right now.",
        },
        {
          key: "need_space" as MomentState,
          label: "Need Space",
          desc: "You need a moment of space to settle yourself.",
        },
        {
          key: "quiet_focus" as MomentState,
          label: "Quiet Focus",
          desc: "You're focused in a gentle, quiet way.",
        },
      ],
      nextBtn: "Continue",
    },
    loading: "Reading your emotional rhythm...",
  },
};

// ─── Shared styles ────────────────────────────────────────────────────────────
const S = {
  wrap: {
    flex: 1,
    overflowY: "auto" as const,
    background: C.bg,
    minHeight: "100%",
    display: "flex" as const,
    flexDirection: "column" as const,
  },
  content: {
    padding: "24px 16px 48px",
    maxWidth: 460,
    margin: "0 auto",
    width: "100%",
    display: "flex" as const,
    flexDirection: "column" as const,
    alignItems: "center" as const,
  },
  iconCircle: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: C.accentSoft,
    border: `2px solid ${C.accentBorder}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 26,
    margin: "0 auto 16px",
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: C.textDark,
    textAlign: "center" as const,
    marginBottom: 6,
    lineHeight: 1.35,
  },
  subtitle: {
    fontSize: 14,
    color: C.textMuted,
    textAlign: "center" as const,
    lineHeight: 1.6,
    marginBottom: 28,
  },
  card: {
    borderRadius: 14,
    background: C.card,
    border: `1px solid ${C.border}`,
    padding: "16px 14px",
    marginBottom: 10,
    width: "100%",
    cursor: "pointer" as const,
    transition: "border-color 0.18s, background 0.18s",
  },
  cardActive: {
    border: `2px solid ${C.accent}`,
    background: C.accentSoft,
  },
  cardLabel: {
    fontSize: 15,
    fontWeight: 600,
    color: C.textDark,
    marginBottom: 4,
  },
  cardDesc: {
    fontSize: 13,
    color: C.textMuted,
    lineHeight: 1.5,
  },
  btn: {
    width: "100%",
    height: 50,
    borderRadius: 14,
    background: C.accent,
    color: C.white,
    fontSize: 15,
    fontWeight: 600,
    border: "none",
    cursor: "pointer" as const,
    marginTop: 8,
    transition: "opacity 0.15s",
  },
  btnDisabled: { opacity: 0.45, cursor: "not-allowed" as const },
  select: {
    flex: 1,
    height: 46,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.card,
    color: C.textDark,
    padding: "0 10px",
    fontSize: 14,
    appearance: "none" as const,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: C.textMuted,
    textAlign: "center" as const,
    marginTop: 6,
  },
  microcopy: {
    fontSize: 13,
    color: C.textMuted,
    textAlign: "center" as const,
    marginTop: 14,
    marginBottom: 4,
    lineHeight: 1.5,
  },
  backBtn: {
    alignSelf: "flex-start" as const,
    background: "none",
    border: "none",
    color: C.textMuted,
    fontSize: 13,
    cursor: "pointer" as const,
    marginBottom: 20,
    padding: 0,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
};

// ─── Component ────────────────────────────────────────────────────────────────
interface Indonesia3BoxProps {
  onBack?: () => void;
  onSubmit: (data: Indonesia3BoxData) => void;
  isLoading?: boolean;
}

// ─── Date of Birth formatting helpers (mirrors EnergyMatchV3Form/MatescanForm) ─
const formatDateValue = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const limitedDigits = digits.slice(0, 8);
  let formatted = "";
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2 || i === 4) {
      formatted += "/";
    }
    formatted += limitedDigits[i];
  }
  return formatted;
};

const validateDateValue = (value: string): string => {
  if (!value) return value;
  const parts = value.split("/");
  if (parts.length === 3) {
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);
    if (isNaN(day)) day = 1;
    if (day < 1) day = 1;
    if (day > 31) day = 31;
    if (isNaN(month)) month = 1;
    if (month < 1) month = 1;
    if (month > 12) month = 12;
    if (isNaN(year)) year = new Date().getFullYear();
    if (year < 1900) year = 1900;
    if (year > 2099) year = 2099;
    return `${day.toString().padStart(2, "0")}/${month.toString().padStart(2, "0")}/${year}`;
  }
  return value;
};

const Indonesia3Box: React.FC<Indonesia3BoxProps> = ({
  onBack,
  onSubmit,
  isLoading = false,
}) => {
  const language = useIndonesiaV2Language();
  const [screen, setScreen] = useState<Screen>("box1");
  const [calmType, setCalmType] = useState<InnerCalmType | null>(null);
  const [dob, setDob] = useState("");
  const [momentState, setMomentState] = useState<MomentState | null>(null);

  const t = useLaneCopy("id3box", CONTENT);

  const isDobComplete = /^\d{2}\/\d{2}\/\d{4}$/.test(dob);
  const dobFormatted = isDobComplete
    ? (() => {
        const [d, m, y] = dob.split("/");
        return `${d}-${m}-${y}`;
      })()
    : null;

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const oldFormatted = rawValue;
    const newFormatted = formatDateValue(rawValue);
    setDob(newFormatted);
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPosition;
        if (cursorPosition === 3 && newFormatted.length > oldFormatted.length) {
          newCursorPos = 4;
        } else if (
          cursorPosition === 6 &&
          newFormatted.length > oldFormatted.length
        ) {
          newCursorPos = 7;
        }
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  function handleBox3Submit() {
    if (!calmType || !dobFormatted || !momentState) return;
    onSubmit({
      inner_calm_type: calmType,
      dob: dobFormatted,
      moment_state: momentState,
    });
  }

  // ── Screen: Box 1 ────────────────────────────────────────────────────────
  if (screen === "box1") {
    return (
      <div style={S.wrap}>
        <div style={S.content}>
          {onBack && (
            <button style={S.backBtn} onClick={onBack}>
              {t.back}
            </button>
          )}
          <div style={S.iconCircle}>{t.box1.icon}</div>
          <h1 style={S.title}>{t.box1.title}</h1>
          <p style={S.subtitle}>{t.box1.subtitle}</p>

          {t.box1.options.map((opt) => {
            const isActive = calmType === opt.key;
            return (
              <div
                key={opt.key}
                style={{ ...S.card, ...(isActive ? S.cardActive : {}) }}
                onClick={() => setCalmType(opt.key)}
              >
                <div style={S.cardLabel}>{opt.label}</div>
                <div style={S.cardDesc}>{opt.desc}</div>
              </div>
            );
          })}

          <button
            style={{ ...S.btn, ...(calmType ? {} : S.btnDisabled) }}
            disabled={!calmType}
            onClick={() => setScreen("box2")}
          >
            {t.box1.nextBtn}
          </button>
        </div>
      </div>
    );
  }

  // ── Screen: Box 2 (DOB) ──────────────────────────────────────────────────
  if (screen === "box2") {
    return (
      <div style={S.wrap}>
        <div style={S.content}>
          <button style={S.backBtn} onClick={() => setScreen("box1")}>
            {t.back}
          </button>
          <div style={S.iconCircle}>{t.box2.icon}</div>
          <h1 style={S.title}>{t.box2.title}</h1>
          <p style={S.subtitle}>{t.box2.subtitle}</p>

          {/* Single DOB input: DD / MM / YYYY */}
          <div style={{ width: "100%", marginBottom: 6 }}>
            <input
              type="text"
              inputMode="numeric"
              placeholder={t.box2.dobPlaceholder}
              value={dob}
              onChange={handleDobChange}
              onBlur={(e) => setDob(validateDateValue(e.target.value))}
              style={{ ...S.select, textAlign: "center", width: "100%" }}
            />
          </div>

          <p style={S.microcopy}>{t.box2.microcopy}</p>

          <button
            style={{
              ...S.btn,
              ...(dobFormatted ? {} : S.btnDisabled),
              marginTop: 20,
            }}
            disabled={!dobFormatted}
            onClick={() => setScreen("box3")}
          >
            {t.box2.nextBtn}
          </button>
        </div>
      </div>
    );
  }

  // ── Screen: Box 3 (Moment State) ─────────────────────────────────────────
  if (screen === "box3") {
    return (
      <div style={S.wrap}>
        <div style={S.content}>
          <button style={S.backBtn} onClick={() => setScreen("box2")}>
            {t.back}
          </button>
          <div style={S.iconCircle}>{t.box3.icon}</div>
          <h1 style={S.title}>{t.box3.title}</h1>
          <p style={S.subtitle}>{t.box3.subtitle}</p>

          {t.box3.options.map((opt) => {
            const isActive = momentState === opt.key;
            return (
              <div
                key={opt.key}
                style={{ ...S.card, ...(isActive ? S.cardActive : {}) }}
                onClick={() => setMomentState(opt.key)}
              >
                <div style={S.cardLabel}>{opt.label}</div>
                <div style={S.cardDesc}>{opt.desc}</div>
              </div>
            );
          })}

          <button
            style={{ ...S.btn, ...(momentState ? {} : S.btnDisabled) }}
            disabled={!momentState}
            onClick={handleBox3Submit}
          >
            {t.box3.nextBtn}
          </button>
        </div>
      </div>
    );
  }

  // ── Loading state ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={S.wrap}>
        <div
          style={{
            ...S.content,
            justifyContent: "center",
            minHeight: 340,
            paddingTop: 80,
          }}
        >
          <div
            style={{
              ...S.iconCircle,
              width: 72,
              height: 72,
              fontSize: 32,
              marginBottom: 24,
            }}
          >
            🌿
          </div>
          <p
            style={{
              fontSize: 15,
              color: C.textMuted,
              textAlign: "center",
              lineHeight: 1.65,
            }}
          >
            {t.loading}
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 28 }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: C.accent,
                  opacity: 0.5,
                  animation: `pulse-dot 1.4s ease-in-out ${i * 0.22}s infinite`,
                }}
              />
            ))}
          </div>
          <style>{`
            @keyframes pulse-dot {
              0%, 100% { opacity: 0.25; transform: scale(0.85); }
              50% { opacity: 0.9; transform: scale(1.1); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return null;
};

export default Indonesia3Box;
